/**
 * Branding Import/Export Utilities
 *
 * Handles exporting branding config to JSON file
 * and importing from JSON file with Zod validation.
 */

import { BrandingConfigSchema } from "@tatvex/config";
import type { BrandingConfig } from "@tatvex/config";

// ============================================================================
// Export
// ============================================================================

/**
 * Export branding config as a downloadable JSON file.
 */
export function exportBrandingJson(branding: BrandingConfig): void {
  const blob = new Blob([JSON.stringify(branding, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "branding.json";
  anchor.click();
  URL.revokeObjectURL(url);
}

// ============================================================================
// Import
// ============================================================================

/** Result of branding import */
export interface ImportResult {
  readonly success: true;
  readonly data: BrandingConfig;
}

/** Error result of branding import */
export interface ImportError {
  readonly success: false;
  readonly error: string;
}

export type ImportBrandingResult = ImportResult | ImportError;

/**
 * Import branding config from a JSON file.
 * Validates against BrandingConfigSchema.
 */
export function importBrandingJson(file: File): Promise<ImportBrandingResult> {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (): void => {
      try {
        const raw = JSON.parse(reader.result as string) as unknown;
        const parsed = BrandingConfigSchema.safeParse(raw);

        if (parsed.success) {
          resolve({ success: true, data: parsed.data });
        } else {
          const messages = parsed.error.errors.map((e) => `${e.path.join(".")}: ${e.message}`);
          resolve({
            success: false,
            error: `Validation errors: ${messages.join("; ")}`,
          });
        }
      } catch {
        resolve({ success: false, error: "Invalid JSON format" });
      }
    };

    reader.onerror = (): void => {
      resolve({ success: false, error: "Failed to read file" });
    };

    reader.readAsText(file);
  });
}
